/**
 * useLivePricing — Feb 2026 SSoT hook.
 *
 * Fetches the current All-Inclusive Pricing tier from
 * `/api/pricing/tests` and merges it over the bundled fallback
 * prices in `siteData.TESTS`. This way admin price edits reach
 * every customer surface (booking cards, homepage, landing pages)
 * without a code push. The bundled fallback keeps the site
 * functional if the endpoint is briefly unavailable.
 *
 * Usage:
 *   const tests = useLivePricing();   // array shaped like TESTS
 *   // …render tests[i].retail / online_prepaid / online / regular
 *
 * Cost: one GET per page render. The endpoint is public + fast +
 * safe to cache; adds a small React Query later if we need to
 * dedupe across sibling components.
 */
import { useEffect, useState } from "react";
import { TESTS } from "@/lib/siteData";
import api from "@/lib/api";


export function useLivePricing() {
  const [live, setLive] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/pricing/tests")
      .then(({ data }) => { if (!cancelled) setLive(data?.prices || null); })
      .catch(() => { /* keep fallback */ });
    return () => { cancelled = true; };
  }, []);

  // Merge live prices over the bundled fallback. Every price field
  // (`retail`, `online_prepaid`, `member`, `online`, `regular`) is set
  // to the same all-inclusive tier — the 20% online-prepaid discount
  // is folded into the single tier under this rollout.
  if (!live) return TESTS;
  return TESTS.map((t) => {
    const cents = live[t.id]?.cents;
    if (!cents) return t;
    const dollars = Math.round(cents / 100);
    return {
      ...t,
      retail:         dollars,
      online_prepaid: dollars,
      member:         dollars,
      online:         dollars,
      regular:        dollars,
      save:           0,
      online_savings: 0,
    };
  });
}
